import { TagBoxCoordMapper } from '../TagBoxCoordMapper.js';
import { createTagBoxView, tagBoxSelector } from '../view/TagBoxView.js';
import { attachKeywordAutocomplete } from '../../keywordAutocomplete/attachKeywordAutocomplete.js';   // P12 4b (parallel compare)
import { applyHiddenHighlight } from '../../keywordAutocomplete/liveHighlight.js';                      // P12 4b (parallel compare)
import { setCaretCharOffset } from '../../keywordAutocomplete/caretWord.js';                            // P12: caret-to-end on focus
import { centeredInputLeft, clampInputToViewport } from '../tagInputPosition.js';
import { createDisposers } from '../../core/disposers.js';
import { insertPlainText, pastedText } from '../../core/plainTextPaste.js';
import { keywordLine } from '../keywordPaste.js';

function formatTagTime(s) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

function parseTagTime(str) {
    const parts = (str ?? '').trim().split(':').map(Number);
    if (!parts.length || parts.some(isNaN)) return null;
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    return null;
}

export class TagBoxJQueryAdapter {
    #$tagLayer = null;
    #view = null;               // TagBoxView — presentation seam (phase tag-view-extraction)
    #docId = null;
    #mediator = null;           // injected at init (was a direct singleton import — L3 Entwirrung)
    #isDrawing                = () => false;
    #isVideoContext           = () => false;
    #getTagStartTime          = () => null;
    #isChrome                 = () => false;
    #keywordSource            = null;           // P12: injected quicklist source for the keyword field's autocomplete
    #getHiddenKeywords        = () => [];       // P12: injected hidden-keyword list for the keyword field's live highlight
    #readOnly                 = false;          // tagsRead without tagsWrite: show tags, never publish a change
    #draggable = null;
    #resizable = null;
    #mouseshakeHandler = null;
    #disposers = null;          // DisposerBag — collects teardown, called in destroy()
    #activeInputDisposers = null;  // per-active-input DisposerBag (plugins/listeners of the open panel)

    #state = {
        lastLevel:      2,
        levelSelecting: false,
        dragging:       false,
        resizing:       false,
        inputResizing:  false,
    };

    // Creates the tag layer as a child of $container.
    // All service callbacks and interaction adapters are injected here.
    init($container, {
        docId                    = null,
        mediator                 = null,
        isDrawing                = () => false,
        isVideoContext           = () => false,
        getTagStartTime          = () => null,
        isChrome                 = () => false,
        keywordSource            = null,
        getHiddenKeywords        = () => [],
        readOnly                 = false,
    } = {}, { draggable = null, resizable = null } = {}) {
        this.#docId                    = docId;
        this.#mediator                 = mediator;
        this.#isDrawing                = isDrawing;
        this.#isVideoContext           = isVideoContext;
        this.#getTagStartTime          = getTagStartTime;
        this.#isChrome                 = isChrome;
        this.#keywordSource            = keywordSource;
        this.#getHiddenKeywords        = getHiddenKeywords;
        this.#readOnly                 = readOnly;
        this.#draggable = draggable;
        this.#resizable = resizable;
        this.#disposers = createDisposers();
        this.#$tagLayer = window.$('<div/>').appendTo($container);
        this.#disposers.add(() => { this.#$tagLayer?.remove(); this.#$tagLayer = null; });
        this.#view = createTagBoxView({ $tagLayer: this.#$tagLayer });
        this.#mouseshakeHandler = () => this.hideActiveInput();
        window.addEventListener('mouseshake', this.#mouseshakeHandler);
        this.#disposers.add(() => {
            window.removeEventListener('mouseshake', this.#mouseshakeHandler);
            this.#mouseshakeHandler = null;
        });
    }

    destroy() {
        this.#activeInputDisposers?.dispose();   // tear down an open input panel's plugins first
        this.#activeInputDisposers = null;
        this.#disposers?.dispose();
        this.#disposers = null;
    }

    getTagLayer()         { return this.#$tagLayer; }

    isDragging()          { return this.#state.dragging; }
    setDragging(v)        { this.#state.dragging = v; }

    isResizing()          { return this.#state.resizing; }
    setResizing(v)        { this.#state.resizing = v; }

    isInputResizing()     { return this.#state.inputResizing; }
    setInputResizing(v)   { this.#state.inputResizing = v; }

    isLevelSelecting()    { return this.#state.levelSelecting; }
    setLevelSelecting(v)  { this.#state.levelSelecting = v; }

    getLastLevel()        { return this.#state.lastLevel; }
    setLastLevel(v)       { this.#state.lastLevel = v; }

    setLayerInteractive(on) {
        if (!on) this.hideActiveInput();
        this.#$tagLayer
            .css('pointer-events', on ? '' : 'none')
            .css('opacity',        on ? '' : 0);
    }

    // Activates the input panel on the given tagBox.
    // Replaces showTextBox in documents.js.
    showInput($tagBox) {
        if (this.isLevelSelecting() || this.isResizing() || this.isInputResizing() || this.isDragging())
            return;

        if (this.#isDrawing()) {
            $tagBox.css('z-index', 1002);
            this.repositionInput($tagBox);
            return;
        }

        if ($tagBox.hasClass('active')) {
            this.repositionInput($tagBox);
            return;
        }

        this.hideActiveInput();

        if (this.#readOnly) {
            // Read-only: just show the keywords — no picker, no resize, no focus, nothing published.
            $tagBox.addClass('active').css('z-index', 1002).children('.input').stop(true, true).fadeIn(100);
            this.repositionInput($tagBox);
            return;
        }

        const $box   = $tagBox.addClass('active').css('z-index', 1002);
        const $input = $box.children('.input');
        const $ce    = $box.find('.keywords-ce');   // the keyword field the user types in
        const $sel   = $box.find('select');
        const tagId  = $box.data('id');

        // (P12 polish) Focus the keyword field with the caret at the END (ready to append) — plain .focus() drops the
        // caret at the start. Reused for the initial focus and for returning from the level picker (selectmenu close).
        const focusCeAtEnd = () => {
            $ce.focus();
            setCaretCharOffset($ce[0], ($ce[0].textContent ?? '').length);
        };

        // One DisposerBag per active input: each setup registers its own teardown here; hideActiveInput
        // disposes the bag → setup/teardown stay symmetric and in one place (also fixes the leaked
        // autocompleteopen listener, which the old code re-added on every activation without removing).
        const bag = this.#activeInputDisposers = createDisposers();

        // Escape in the active box asks the core to discard or reset it (plan bugfix_2026-10-06_permission-tagging,
        // phase speichern, stage 5). Not when the Escape was already used: the keyword suggestions close on it and
        // call preventDefault; the level picker closes on it WITHOUT preventDefault, so Escapes from the picker are
        // left to the picker. Bubbling phase — the field's own handlers run first.
        const onEscape = (ev) => {
            if (ev.key !== 'Escape' || ev.defaultPrevented) { return; }
            if (window.$(ev.target).closest('.ui-selectmenu-button, .ui-selectmenu-menu').length) { return; }
            ev.preventDefault();
            // The Escape is ours — its keyup must not reach the popup's own Escape handler (documents.js, keyup on
            // body), which would close the popup: a discarded empty box is gone by then, the focus fell back to body,
            // and the handler no longer sees a tag box as the target. Capture phase on document runs before it.
            const swallowKeyup = (up) => {
                document.removeEventListener('keyup', swallowKeyup, true);
                if (up.key === 'Escape') { up.stopPropagation(); }
            };
            document.addEventListener('keyup', swallowKeyup, true);
            this.#mediator.publish('tag:dom:revert:request', { tagId, docId: this.#docId });
        };
        $box[0].addEventListener('keydown', onEscape);
        bag.add(() => $box[0].removeEventListener('keydown', onEscape));

        // (P12) The keyword field ($ce) has its own live highlight + autocomplete (wired in #wireCe). The tag box
        // no longer uses hwt/dynAutocomplete here — the footer table keeps those via basic.js.

        this.#mediator.publish('tag:dom:input:focus', { tagId, docId: this.#docId });

        $input.stop(true, true).fadeIn(100, () => {
            $sel.selectmenu({
                appendTo: $input[0],
                change:   (event, ui) => { this.setLastLevel(ui.item.value); },
                close:    ()          => { focusCeAtEnd(); },
                icons:    { button: 'ui-icon-caret-1-s' },
            });
            bag.add(() => { if ($sel.selectmenu('instance')) { $sel.selectmenu('destroy'); } });
            $sel.selectmenu('widget')
                // topo2: mark our level-picker button so the topo2-solid rule targets only this selectmenu
                // (not any other jQuery UI selectmenu on the page). Replaces the legacy gray span[role=combobox] rule.
                .addClass('topo2-tag-level')
                .on('mouseenter', ()  => { this.setLevelSelecting(true); })
                .on('mouseleave', ()  => { setTimeout(() => this.setLevelSelecting(false), 200); });

            $input.resizable({
                maxHeight: this.#isVideoContext() ? 155 : 125,
                maxWidth: 300, minHeight: 17, minWidth: 125, handles: 'none',
                start:  () => { this.setInputResizing(true); },
                resize: () => { this.#mediator.publish('tag:dom:content:changing', { tagId, docId: this.#docId }); },
                stop:   () => { this.setInputResizing(false); },
            });
            bag.add(() => { if ($input.hasClass('ui-resizable')) { $input.resizable('destroy'); } });

            focusCeAtEnd();   // focus the field with the caret at the end

            // (P12) Alt+↓ opens the level picker from the keyword field. Bound on $ce (where the focus is → the keydown
            // actually arrives) and triggered via the selectmenu API. The old legacy attempt failed twice over: it sat
            // on a non-focused node (no keydown reached it) AND used an empty DOM-sibling selector. Namespaced so it
            // doesn't clash with the autocomplete's own keydown (that one bails while its menu is closed).
            $ce.on('keydown.altlevel', (ev) => {
                if (ev.altKey && ev.key === 'ArrowDown') {
                    ev.preventDefault();
                    if ($sel.selectmenu('instance')) {
                        $sel.selectmenu('open');
                        $sel.selectmenu('widget').trigger('focus');   // the picker needs focus for arrow-key nav; close → focusCeAtEnd
                    }
                }
            });
            bag.add(() => { $ce.off('keydown.altlevel'); });

            const $ti = $box.find('.tagTimeInput');
            if ($ti.length) {
                const secs = this.#getTagStartTime(tagId);
                $ti.val(secs != null ? formatTagTime(secs) : '').show();
            }
        });
        this.repositionInput($tagBox);
    }

    // The id of the box whose input is open (being edited), or null.
    getActiveTagId() {
        const $tb = this.#$tagLayer.find('.tagBox.active');
        return $tb.length ? $tb.data('id') : null;
    }

    // Reads the active box's text and level into the store (tag:dom:text:changed) WITHOUT closing its input. Called
    // before anything is sent, so text typed into a box that was never left still goes out (analysis W1/W2). Unlike
    // hideActiveInput it does not require the input to be visible.
    harvestActiveInput() {
        if (this.#readOnly) { return; }
        const typed = this.readActiveInput();
        if (!typed) { return; }
        this.#mediator.publish('tag:dom:text:changed', { tagId: typed.tagId, newText: typed.text, newLevel: typed.level, docId: this.#docId });
    }

    // What the active box's input holds right now — { tagId, text, level } — or null. Reads only, publishes nothing.
    readActiveInput() {
        const $tb = this.#$tagLayer.find('.tagBox.active');
        const $ce = $tb.find('.keywords-ce');
        if (!$tb.length || !$ce.length) { return null; }
        return { tagId: $tb.data('id'), text: $ce.text() ?? '', level: this.#readLevel($tb) };
    }

    // The level shown by the picker (jQuery UI selectmenu text), else the plain <select> (read-only / not built yet).
    #readLevel($scope) {
        const stext = $scope.find('span[role=combobox] > span.ui-selectmenu-text');
        return stext.length ? stext.html() : $scope.find('select').val();
    }

    // The box is no longer the active one: time field hidden, back in the normal stacking order.
    #deactivateBox($tb) {
        $tb.find('.tagTimeInput').hide();
        $tb.css('z-index', '').removeClass('active');
    }

    // Closes the active input WITHOUT taking over what was typed (stage 5: discard / reset) — hideActiveInput would
    // publish it as a text change.
    cancelActiveInput() {
        const $tb = this.#$tagLayer.find('.tagBox.active');
        if (!$tb.length) { return; }
        const disposers = this.#activeInputDisposers;
        this.#activeInputDisposers = null;
        $tb.find('.input').hide().css({ right: '', margin: '' });
        disposers?.dispose();
        this.#deactivateBox($tb);
    }

    // Puts the cursor back into the active box's keyword field (after "keep editing" in the discard/reset dialog).
    focusActiveInput() {
        const $ce = this.#$tagLayer.find('.tagBox.active .keywords-ce');
        if (!$ce.length) { return; }
        $ce.focus();
        setCaretCharOffset($ce[0], ($ce[0].textContent ?? '').length);
    }

    // Shows a model in its box again: keyword text, level, geometry (stage 5: reset to the saved state).
    showModelInBox(model) {
        const $tb = this.getTagBoxById(model.id);
        if (!$tb.length) { return; }
        $tb.find('.keywords-ce').text(model.text ?? '');
        $tb.find('select').val(String(model.level ?? 2));
        this.repositionAllTagDoms([model]);
    }

    // Removes a box from the layer (stage 5: a new box is discarded).
    removeTagBox(tagId) {
        this.getTagBoxById(tagId).remove();
    }

    // What every box shows in its keyword field — inactive boxes keep theirs too, only hidden (fehler-sichtbar F3).
    readBoxTexts() {
        return this.#$tagLayer.find('.tagBox').get()
            .map((el) => ({ tagId: window.$(el).data('id'), ce: el.querySelector('.keywords-ce') }))
            .filter(({ ce }) => ce !== null)
            .map(({ tagId, ce }) => ({ tagId, text: ce.textContent ?? '' }));
    }

    // Deactivates the currently active input panel, reads text/level, and publishes tag:dom:text:changed.
    // Replaces hideTextBox in documents.js.
    hideActiveInput() {
        const $tb = this.#$tagLayer.find('.tagBox.active');
        if (!$tb.length) { return; }

        const $input = $tb.find('.input:visible');
        const $ce    = $input.find('.keywords-ce');   // (P12 2a) value anchor is the new field

        // The active input's teardown (hwt, autocomplete, selectmenu, resizable, autocompleteopen) was
        // registered in showInput's bag — dispose it in one place. The visually-sensitive teardowns run
        // in the fadeOut callback, matching the old timing (hwt highlight stays until the panel is gone).
        const disposers = this.#activeInputDisposers;
        this.#activeInputDisposers = null;

        // Read-only: nothing can have changed — and without a level picker newLevel would read as undefined,
        // which TaggingCore would take for a change and queue a save.
        if ($input.length && !this.#readOnly) {
            const newText  = $ce.text() ?? '';
            const newLevel = this.#readLevel($input);
            const tagId    = $tb.data('id');

            $input.fadeOut(100, () => {
                disposers?.dispose();
                $tb.find('.input').css({ right: '', margin: '' });
            });

            this.#mediator.publish('tag:dom:text:changed', { tagId, newText, newLevel, docId: this.#docId });
        } else {
            $input.hide();
            disposers?.dispose();
        }

        this.#deactivateBox($tb);
    }

    // Repositions the .input panel relative to the tagBox to stay within viewport.
    // Replaces TextBoxPosition in documents.js.
    // Public for now — called from CreateTagBox draggable/resizable until Schritte 5+6 move them into the adapter.
    repositionInput($tBox) {
        if (this.isDragging() || this.isResizing()) { return; }

        const $ = window.$;
        const $input = $tBox.find('.input');
        if (!$tBox.hasClass('tagBox')) { return; }

        try {
            // Phase 1 — center the input over the box (measure → compute → apply → re-measure).
            $input.css({ 'left': '' });
            const centered = centeredInputLeft(
                parseInt($input.css('left')),
                $input[0].getBoundingClientRect(),
                $tBox[0].getBoundingClientRect(),
            );
            if (centered !== parseInt($input.css('left'))) { $input.css({ 'left': centered }); }

            // Phase 2 — clamp into the viewport and drop below the box (pure decision, DOM apply).
            const ibr = $input[0].getBoundingClientRect();
            const pos = clampInputToViewport({
                viewportLeft:  $input.offset().left - $(window).scrollLeft(),
                currentLeft:   parseInt($input.css('left')),
                inputRight:    ibr.right,
                inputWidth:    ibr.width,
                viewportWidth: document.body.clientWidth,
                tagBoxHeight:  $tBox.height(),
            });
            if (pos.left !== undefined) { $input.css({ 'left': pos.left }); }
            if (pos.top  !== undefined) { $input.css({ 'top':  pos.top }); }
        } catch (err) {
            console.log('repositionInput exception: ' + err);
        }
    }

    // Creates the full tag box DOM structure with internal event bindings.
    // Returns { $tagBox, $input } — caller ($CreateTagBox) appends to layer and adds draggable/resizable.
    // text/level seed the initial textarea value and select level.
    createTagBox(id, pix, { text, level } = {}) {
        const $ = window.$;

        const { $tagBox, $input, $ce, $select, $closeButton, $timeInput } = this.#view.build(id, pix, {
            text,
            level: level || this.getLastLevel() || 2,
            isVideo: this.#isVideoContext(),
            readOnly: this.#readOnly,
        });

        // The box's CURRENT id, read from the DOM instead of captured. A box can be re-labelled after a save —
        // it is drawn with a negative placeholder and adopts the real tag_id from the response — and a handler
        // that had closed over the id would keep publishing the old one (plan phase `js-echte-tagid`).
        const currentId = () => $tagBox.data('id');

        // View builds the DOM; the adapter binds all behavior (handlers, drag/resize) onto the returned elements.
        $closeButton?.on('click', () => {
            this.#mediator.publish('tag:dom:close:click', { tagId: currentId(), docId: this.#docId });
        });

        if ($timeInput) {
            $timeInput
                .on('mousedown', ev => ev.stopPropagation())
                .on('focus', () => {
                    this.#mediator.publish('tag:dom:time:focus', { tagId: currentId(), docId: this.#docId });
                })
                .on('keydown', ev => {
                    if (ev.key === 'Enter') {
                        ev.preventDefault();
                        const secs = parseTagTime($timeInput.val());
                        if (secs !== null) {
                            $timeInput.val(formatTagTime(secs));
                            this.#mediator.publish('tag:dom:time:changed', { tagId: currentId(), startTime: secs, docId: this.#docId });
                        }
                        return;
                    }
                    if (!/[\d:]/.test(ev.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab'].includes(ev.key))
                        ev.preventDefault();
                })
                .on('blur', () => {
                    const secs = parseTagTime($timeInput.val());
                    if (secs !== null)
                        this.#mediator.publish('tag:dom:time:changed', { tagId: currentId(), startTime: secs, docId: this.#docId });
                });
        }

        $select
            ?.change((ev) => {
                this.setLastLevel($(ev.currentTarget).val());
                this.#mediator.publish('tag:dom:content:changing', { tagId: currentId(), docId: this.#docId });
                this.setLevelSelecting(false);
            })
            .on('blur', () => {
                this.setLevelSelecting(false);
            });

        // NB (P12 2b): the old textarea carried these interaction handlers, dropped with it (the $ce ran without them
        // in the parallel/adopt tests): mousedown→stopPropagation, focus→setLevelSelecting(false), drop/keyup→
        // 'tag:dom:content:changing', keydown/input→auto-grow (now CSS). If a field-click ever starts a spurious draw,
        // a stuck level-select, or other tags not committing while typing, port the matching handler onto $ce in #wireCe.

        $tagBox
            .on('mouseenter', () => this.showInput($tagBox))
            .on('blur', () => {
                if (!(this.isLevelSelecting() || this.isDragging() || this.isResizing() || this.isInputResizing())) {
                    $tagBox.css('z-index', '');
                }
            });

        // Bind after $tagBox is defined — these closures need the $tagBox reference.
        // Only with a drag adapter: without one (read-only) the box has no jQuery UI draggable to toggle, and
        // calling its methods would throw.
        if (this.#draggable) {
            $input
                .mouseenter(() => { $tagBox.draggable('disable').css('opacity', 1); })
                .mouseleave(() => { $tagBox.draggable('enable').css('opacity', ''); });
        }

        this.#attachResize($tagBox);
        this.#attachDrag($tagBox);
        this.#wireCe($ce);   // P12 — wire the keyword field's autocomplete + live hidden-keyword highlight

        return { $tagBox, $input };
    }

    // (P12) Wire the keyword field ($ce, contenteditable): the live hidden-keyword highlight + the keyword
    // autocomplete. Guarded so it is inert without a keywordSource. Replaces the old textarea's hwt/dynAutocomplete.
    #wireCe($ce) {
        if (!$ce || !$ce.length) { return; }
        const ce = $ce[0];
        // Hidden keywords the autocomplete has SEEN (quicklist flags hidden:true per item). The static
        // getHiddenKeywords() only knows keywords already attached to loaded docs, so a keyword freshly picked from
        // the suggestion list would stay white until save — harvest the quicklist's hidden flags so it goes red at once.
        const discoveredHidden = new Set();
        const isHidden = (kw) => this.#getHiddenKeywords().includes(kw) || discoveredHidden.has(kw);
        const highlight = () => applyHiddenHighlight(ce, isHidden);
        $ce.on('input', () => { highlight(); });
        highlight();   // seed text may already contain hidden keywords
        // Paste as plain text on one line (boss's ticket T206): the markup of Word or LibreOffice stays out, its line
        // breaks become keyword separators. The insert ends with an "input" event, so highlight and autocomplete follow.
        if (!this.#readOnly) {
            $ce.on('paste', (e) => {
                const event = e.originalEvent;
                if (!event.clipboardData) { return; }
                event.preventDefault();
                insertPlainText(ce, keywordLine(pastedText(event)));
            });
        }
        if (this.#keywordSource && !this.#readOnly) {   // read-only: highlight hidden keywords, offer no suggestions
            const harvestingSource = async (term) => {
                const items = await this.#keywordSource(term);
                items.forEach((it) => { if (it.hidden) { discoveredHidden.add(String(it.label).trim()); } });
                return items;
            };
            const ac = attachKeywordAutocomplete(ce, {
                source: harvestingSource,
                minLength: 3,
                onChange: () => { highlight(); },
            });
            this.#disposers.add(() => ac.destroy());
        }
    }

    // Build a tag box, append it to the layer, and (for very short boxes) hide the SE-resize handle.
    // Was documents.js UIElementFactory.CreateTagBox + the data.height<25 tweak (M2).
    createAndAppendTagBox(id, pix, { text, level, hideResizeHandle = false } = {}) {
        const { $tagBox } = this.createTagBox(id, pix, { text, level });
        $tagBox.appendTo(this.#$tagLayer);
        if (hideResizeHandle) { $tagBox.find('div.ui-resizable-se').first().css('visibility', 'hidden'); }
        return $tagBox;
    }

    // Find a tag box in THIS layer by its id (layer-scoped → no cross-popup id collision). (M1b)
    getTagBoxById(id) { return this.#$tagLayer.find(tagBoxSelector(id)); }

    /**
     * Re-label a box after the server has told us its real tag_id (a box drawn in this session carried a negative
     * placeholder until then). The DOM is the single source of the id — every handler reads it live — so writing
     * it here is enough; nothing has to be re-bound.
     */
    setTagBoxId(oldId, newId) {
        const $box = this.getTagBoxById(oldId);
        if (!$box.length) { return false; }
        $box.attr('data-id', newId).data('id', newId);
        return true;
    }

    // Pixel draw-rect → percent coords relative to this tag layer (was documents.js pixToPercent + $tagLayer dims). (M1b)
    pixToPercentData(pix) {
        const pct = TagBoxCoordMapper.pixToPercent(
            { left: pix.l, top: pix.t, width: pix.w, height: pix.h },
            this.#$tagLayer.width(), this.#$tagLayer.height(),
        );
        return { l: pct.left, t: pct.top, w: pct.width, h: pct.height };
    }

    // Activate a freshly drawn box: open its input panel. Chrome needs a prior blur (was browserInfo.type==='Chrome'
    // in documents.js) — via the injected isChrome service, so non-Chrome behavior is unchanged. (M1b)
    activateDrawnBox($tagBox) {
        if (!$tagBox || !$tagBox.length) { return; }
        if (this.#isChrome()) { $tagBox.trigger('blur'); }
        this.showInput($tagBox);
    }

    // Attaches the resize adapter to a tag box (guarded — no-op without a resizable adapter).
    // The id is read from the box on each event, never captured — see currentId() in createTagBox.
    #attachResize($tagBox) {
        if (!this.#resizable) { return; }
        const currentId = () => $tagBox.data('id');
        this.#resizable.attach($tagBox, {
            containment: this.#$tagLayer,
            handles:     'n, e, s, w, se',
            onStart: () => {
                this.setResizing(true);
                const activeTagId = this.#dimActiveBox();
                this.#mediator.publish('tag:dom:resize:start', { tagId: currentId(), activeTagId, docId: this.#docId });
                this.hideActiveInput();
            },
            onResize: () => { this.repositionInput($tagBox); },
            onStop: () => {
                if (!this.isResizing()) { return; }
                this.setResizing(false);
                this.#$tagLayer.find('.tagBox.active').css({ opacity: '', 'box-shadow': '' });
                this.showInput($tagBox);
                this.#mediator.publish('tag:dom:resize:stop', { tagId: currentId(), docId: this.#docId });
            },
        });
    }

    // A drag/resize starts: the box being edited dims (its input closes right after) — returns its id for the event.
    #dimActiveBox() {
        const $active = this.#$tagLayer.find('.tagBox.active');
        $active.css({ opacity: '.75', 'box-shadow': 'none' });
        return $active.data('id') ?? null;
    }

    // Attaches the drag adapter to a tag box (guarded — no-op without a draggable adapter).
    // The id is read from the box on each event, never captured — see currentId() in createTagBox.
    #attachDrag($tagBox) {
        if (!this.#draggable) { return; }
        const currentId = () => $tagBox.data('id');
        this.#draggable.attach($tagBox, {
            getBounds: () => ({ width: this.#$tagLayer.width(), height: this.#$tagLayer.height() }),
            onStart: () => {
                this.setDragging(true);
                const activeTagId = this.#dimActiveBox();
                this.#mediator.publish('tag:dom:drag:start', { tagId: currentId(), activeTagId, docId: this.#docId });
                this.hideActiveInput();
            },
            onDrag: () => { this.repositionInput($tagBox); },
            onStop: () => {
                if (!this.isDragging()) { return; }
                this.setDragging(false);
                this.#$tagLayer.find('.tagBox').css({ opacity: '', 'box-shadow': '' });
                this.showInput($tagBox);
                this.#mediator.publish('tag:dom:drag:stop', { tagId: currentId(), docId: this.#docId });
            },
        });
    }

    // Presentation effects delegate to TagBoxView (the presentation seam, phase tag-view-extraction).
    applyTagFilter(tags, filter) { this.#view.applyTagFilter(tags, filter); }

    flashTagBox(tagId, success) { this.#view.flashTagBox(tagId, success); }

    // Resolves a jQuery element or percent-data object into { $b, existingId, data, pix }.
    // existingId: the tagBox's data('id') if present, otherwise null — caller computes dbid.
    resolveInputCoords(input) {
        return input instanceof window.$
            ? this.#resolveCoordsFromElement(input)
            : this.#resolveCoordsFromPercentData(input);
    }

    #resolveCoordsFromElement($el) {
        const $b        = $el.closest('.tagBox');
        const existingId = $b.data('id') ?? null;
        const pix       = { l: $b.position().left, t: $b.position().top, w: $b.width(), h: $b.height() };
        const pct       = TagBoxCoordMapper.pixToPercent(
            { left: pix.l, top: pix.t, width: pix.w, height: pix.h },
            this.#$tagLayer.width(), this.#$tagLayer.height()
        );
        return { $b, existingId, data: { left: pct.left, top: pct.top, width: pct.width, height: pct.height }, pix };
    }

    #resolveCoordsFromPercentData(input) {
        const pix  = TagBoxCoordMapper.percentToPx(
            { left: input.l, top: input.t, width: input.w, height: input.h },
            this.#$tagLayer.width(), this.#$tagLayer.height()
        );
        const data = {
            left:     input.l, top: input.t, width: input.w, height: input.h,
            level:    input.level, text: input.text,
            metadata: input.metadata ? JSON.parse(input.metadata) : null,
            // The real box id, or null while the document still lives in document_has_tag (plan `js-echte-tagid`).
            tagId:    input.tagId ?? null,
        };
        return { $b: null, existingId: null, data, pix: { l: pix.left, t: pix.top, w: pix.width, h: pix.height } };
    }

    configureLayer($img) { this.#view.configureLayer($img); }

    repositionAllTagDoms(tags) {
        tags.forEach(tag => {
            const pix = TagBoxCoordMapper.percentToPx(
                { left: tag.left, top: tag.top, width: tag.width, height: tag.height },
                this.#$tagLayer.width(), this.#$tagLayer.height()
            );
            this.#$tagLayer.find(tagBoxSelector(tag.id)).css({
                left:   Math.round(pix.left),
                top:    Math.round(pix.top),
                width:  Math.round(pix.width),
                height: Math.round(pix.height),
            });
        });
    }

}
